import React, {
  Children,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react'
import FlyoutTriggerWrapper from './FlyoutTriggerWrapper'
import PropTypes from 'prop-types'
import { useSetState, useGlobalId, Dropdown } from '@zambezi/sdk/common'

/**
 * @readonly
 * @enum {import('../common/interfaces').ShowOn}
 */
const SHOW_ON = {
  HOVER: 'hover',
  CLICK: 'click',
  FOCUS: 'focus'
}

const SHOW_ON_VALUES = Object.values(SHOW_ON)

/**
 * Create a React Flyout Trigger component
 *
 * @param {import('./interfaces').FlyoutTriggerProps} props
 * @returns {JSX.Element}
 */
function FlyoutTrigger ({
  flyout,
  children,
  showOn,
  position,
  arrowAlign,
  onShow,
  onHide,
  delayShow,
  delayHide,
  isFlyoutShown: propsIsFlyoutShown,
  defaultIsFlyoutShown,
  ...rest
}) {
  const [{ isFlyoutShown, showTrigger }, setState] = useSetState({
    isFlyoutShown:
      propsIsFlyoutShown !== undefined
        ? propsIsFlyoutShown
        : defaultIsFlyoutShown,
    showTrigger: null
  })
  const elementRef = useRef()
  const child = Children.only(children)
  const wrapperId = useGlobalId('zb-flyout-container-')
  const childProps = useMemo(
    () => ({
      ...child.props
    }),
    [child.props]
  )

  const show = useCallback(
    showTrigger => {
      if (propsIsFlyoutShown === undefined) {
        setState({ isFlyoutShown: true, showTrigger })
      } else {
        setState({ showTrigger })
      }
      onShow()
    },
    [onShow, propsIsFlyoutShown]
  )
  const hide = useCallback(() => {
    if (propsIsFlyoutShown === undefined) {
      setState({ isFlyoutShown: false })
    }
    onHide()
  }, [onHide, propsIsFlyoutShown])
  const Wrapper = useMemo(
    () =>
      forwardRef((props, ref) => (
        <FlyoutTriggerWrapper
          ref={ref}
          {...props}
          arrowAlign={arrowAlign}
          shouldFocus={showTrigger === SHOW_ON.CLICK}
          onClose={hide}
          {...rest}
        />
      )),
    [hide, showTrigger, arrowAlign]
  )

  useEffect(componentWillReceiveProps, [propsIsFlyoutShown])

  return (
    <Dropdown
      Trigger={child.type}
      triggerProps={childProps}
      onHide={hide}
      onShow={show}
      isShown={isFlyoutShown}
      wrapperId={wrapperId}
      showOn={showOn}
      position={position}
      delayShow={delayShow}
      delayHide={delayHide}
      withHideOnClickOutside={shouldShowOn(SHOW_ON.CLICK)}
      Wrapper={Wrapper}
      ref={elementRef}
    >
      {flyout}
    </Dropdown>
  )

  function componentWillReceiveProps () {
    if (propsIsFlyoutShown !== undefined) {
      setState({ isFlyoutShown: propsIsFlyoutShown })
    }
  }

  function shouldShowOn (type) {
    return Array.isArray(showOn) ? showOn.indexOf(type) !== -1 : showOn === type
  }
}

FlyoutTrigger.defaultProps = {
  position: FlyoutTriggerWrapper.defaultProps.position,
  delayShow: 150,
  delayHide: 150,
  showOn: SHOW_ON.CLICK,
  defaultIsFlyoutShown: false,
  isFlyoutShown: undefined,
  onShow: () => {},
  onHide: () => {},
  arrowAlign: FlyoutTriggerWrapper.defaultProps.arrowAlign
}

FlyoutTrigger.propTypes = {
  flyout: PropTypes.element,
  delayShow: PropTypes.number,
  delayHide: PropTypes.number,
  position: FlyoutTriggerWrapper.propTypes.position,
  showOn: PropTypes.oneOfType([
    PropTypes.oneOf(SHOW_ON_VALUES.concat(null)),
    PropTypes.arrayOf(PropTypes.oneOf(SHOW_ON_VALUES))
  ]),
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  arrowAlign: FlyoutTriggerWrapper.propTypes.arrowAlign,
  isFlyoutShown: PropTypes.bool,
  defaultIsFlyoutShown: PropTypes.bool
}

export default FlyoutTrigger
